import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { reaction } from 'mobx';

import { ghAnnualUpgradeToken, pathProductLogin } from '~/common/globals';
import removeURLParameter from '~/common/utils/removeUrlParam.js';
import { SALES_PHONE_NUMBER_FORMATTED, LMI_LOGIN_FLOW_ENABLED } from '~/common/utils/consts';
import { FLOWS_ENUM } from '~/common/stores/ui/loginStore';

import Banner from '~/common/components/Banner';
import Centering from '~/common/components/Centering';
import CommonButton from '~/common/components/CommonButton';
import GreyBox from '~/common/components/GreyBox';
import HeaderGroup from '~/common/components/HeaderGroup';
import LoadingSpinner from '~/common/components/LoadingSpinner';
import PlanDetails from '~/views/AnnualUpgrade/components/PlanDetails';
import Subheader from '~/common/components/Subheader';

import { gtm, viewContentEvent, startBuyFlowEvent, beginCheckoutFlowEvent, userInformationEvent, clickCtaEvent } from '~/common/utils/tracking';

import styles from './annual-upgrade.css';

@withRouter
@inject('modalStore', 'loginStore', 'annualUpgradeStore')
@observer
export default class AnnualUpgrade extends Component {
    state = {
        digitalListingAddon: false
    }

    componentDidMount() {
        const { history, loginStore, annualUpgradeStore } = this.props;
        const { pathname, search } = history.location;

        // if auth token in the url
        if (ghAnnualUpgradeToken()) {
            // save token
            loginStore.setAuthToken(ghAnnualUpgradeToken());
            // get user info
            annualUpgradeStore.getUserInfo();
            // "refresh" the page, while removing the convert param
            history.push({
                pathname,
                search: removeURLParameter(search, 'gatok')
            });

            // if auth token is not already saved
        } else if (!loginStore.authToken.length > 0) {
            // set option so login modal logs the user via username
            loginStore.setLoginByUsername(true);
            loginStore.setLogInFlow(FLOWS_ENUM.ANNUAL_UPGRADE);
            this.observeLoginModal();
        }

        gtm(viewContentEvent('upgrade flow', 'checkout', 'upgrade flow'));
    }

    // Once Upgrade Details are available call the gtm events
    autoUpgradeDetails = reaction(
        () => this.props.annualUpgradeStore.upgradeOfferDetails,
        () => {
            const { userInfo } = this.props.loginStore;
            const { currentOfferDetails } = this.props.annualUpgradeStore;

            gtm(userInformationEvent(userInfo));

            if (this.props.annualUpgradeStore.upgradeOfferDetails) {
                gtm(startBuyFlowEvent());
                gtm(beginCheckoutFlowEvent(1, 'upgrade'));
                gtm(beginCheckoutFlowEvent(2, 'upgrade'));
                gtm(beginCheckoutFlowEvent(3, 'upgrade'));
                gtm(beginCheckoutFlowEvent(4, 'upgrade'));
            }

            currentOfferDetails.chargeEstimates.forEach((addon) => {
                if (addon.name === 'Digital Listing Add-on') {
                    this.setState({ digitalListingAddon: addon });
                }
            });
        }
    );

    observeLoginModal = () => {
        const modalFeatureType = LMI_LOGIN_FLOW_ENABLED ? 'loginLmiModal' : 'loginModal';
        if (modalFeatureType) {
            this.triggerLoginModal(modalFeatureType);
        }
    };

    triggerLoginModal = (modalFeatureType) => {
        const { modalStore } = this.props;
        modalStore.setModalOptions({
            childComponentString: modalFeatureType,
            isModalClosable: false,
            modalType: 'xlarge',
            isModalOpen: true
        });
    };

    rejectUpgrade = (ev) => {
        ev.preventDefault();
        const { annualUpgradeStore } = this.props;
        annualUpgradeStore.setUserRejectedUpgrade(true);
        gtm(clickCtaEvent('keep paying more', '', 'annual upgrade'));
        // clear any error message if any
        annualUpgradeStore.setErrorUpgradingToAnnual(false);
    };

    convertToAnnual = (ev) => {
        ev.preventDefault();
        this.props.annualUpgradeStore.convertToAnnual();
    };

    renderUserInfo = () => {
        const { loginStore } = this.props;
        const { userInfo, userFirstNumber } = loginStore;
        const { FirstName, LastName, Email } = userInfo;

        return (
            <div className={styles['account-details']}>
                <Subheader modifier={styles.subheader}>Account Information</Subheader>
                {(FirstName && LastName) && <p>{`${FirstName} ${LastName}`}</p>}
                {userFirstNumber && <p>{userFirstNumber}</p>}
                {Email && <p>{Email}</p>}
            </div>
        );
    };

    renderBanner = () => {
        const { errorUpgradingToAnnual, userUpgradedOrRejected } = this.props.annualUpgradeStore;

        // if user succesfully upgraded or rejected, don't display the banner
        if (userUpgradedOrRejected) return null;

        return (
            <Banner errorState={errorUpgradingToAnnual} modifiers={styles['banner-area']}>
                {errorUpgradingToAnnual ?
                    <Fragment><b>Something went wrong!</b> Please click “Switch to save" to start saving.</Fragment>
                    :
                    <Fragment><b>Save today!</b> Review your plan details below and click “Switch to save" to start saving.</Fragment>
                }
            </Banner>
        );
    };

    renderSecondStep = () => {
        const { userRejectedUpgrade } = this.props.annualUpgradeStore;

        if (userRejectedUpgrade) {
            return (
                <Fragment>
                    <Subheader>If you change your mind…</Subheader>
                    <p>You can still put more money back in your pocket by switching to annual at a later date. Just call {SALES_PHONE_NUMBER_FORMATTED}.</p>
                </Fragment>
            );
        }

        return (
            <Fragment>
                <Subheader>Great choice!</Subheader>
                <p>Welcome to Grasshopper’s annual billing plan. See “<b>offer details</b>” below for more information, or <a href={pathProductLogin()}>log into</a> your account.</p>
            </Fragment>
        );
    };

    render() {
        const { userInfoFetched, totalSavingsForUpgrading, currentOfferDetails,
            upgradeOfferDetails, userUpgradedOrRejected, userAlreadyAnnual, userUpgradedSuccesfully } = this.props.annualUpgradeStore;
        const { digitalListingAddon } = this.state;
        const WrapperClassList = userUpgradedOrRejected ? `${styles['comparison-wrapper']} ${styles['comparison-wrapper--second-step']}` : styles['comparison-wrapper'];
        const totalSavings = (Number(totalSavingsForUpgrading) + upgradeOfferDetails?.discountAmount).toFixed(2);
        const showDiscount = !!upgradeOfferDetails?.discountAmount;

        return (
            <Centering modifier={styles.wrapper}>
                <HeaderGroup modifiers={styles.header}>
                    {userUpgradedSuccesfully ?
                        <h1>You've saved {totalSavingsForUpgrading && `$${totalSavings} `}by switching to an annual plan.</h1>
                        :
                        <Fragment>
                            <h1>Save {totalSavingsForUpgrading && `$${totalSavings} `}when you switch to an annual plan!</h1>
                            <h2>Now is the time to look at ways you can save.</h2>
                        </Fragment>
                    }
                </HeaderGroup>
                {userInfoFetched ?
                    <Fragment>
                        {this.renderBanner()}
                        <div className={WrapperClassList}>
                            {this.renderUserInfo()}
                            {userUpgradedOrRejected ?
                                <GreyBox modifier={styles['second-step']}>
                                    {this.renderSecondStep()}
                                </GreyBox>
                                :
                                <Fragment>
                                    <PlanDetails
                                        digitalListingAddon={digitalListingAddon}
                                        modifier={styles['details-monthly']}
                                        planDetails={currentOfferDetails}
                                        showDiscount={showDiscount}
                                    />
                                    <CommonButton
                                        alternateColor
                                        modifier={styles['keep-plan-button']}
                                        onClick={this.rejectUpgrade}
                                    >
                                        Keep paying more
                                    </CommonButton>
                                    <PlanDetails
                                        digitalListingAddon={digitalListingAddon}
                                        modifier={styles['details-annual']}
                                        planDetails={upgradeOfferDetails}
                                        showDiscount={showDiscount}
                                    />
                                    <p className={styles.save}>{totalSavingsForUpgrading && <Fragment>Save <b>${totalSavings}</b> by switching to an annual plan!</Fragment>}</p>
                                    <CommonButton
                                        disabled={userAlreadyAnnual}
                                        modifier={styles['upgrade-button']}
                                        onClick={this.convertToAnnual}
                                    >
                                        Switch to save
                                    </CommonButton>
                                    <p className={styles.terms}>By clicking  “Switch to save” you are agreeing to the <a href="https://www.goto.com/company/legal/service-descriptions#grasshopper" target="_blank">Terms & Conditions</a>.</p>
                                </Fragment>
                            }
                        </div>
                        <p className={styles.disclaimer}><b>Offer details:</b> Your annual billing cycle will start immediately, and your account will be charged.
                        Today's date will be your annual billing date. You will be reimbursed for the portion of the current month that you haven't used yet.</p>
                    </Fragment>

                    :
                    userAlreadyAnnual ?
                        <Banner errorState={true} modifiers={styles['banner-area']}>
                            <p><b>No action required!</b> Looks like you already have annual billing on your account!</p>
                        </Banner>
                        :
                        <div className={styles.spinner}>
                            <LoadingSpinner />
                        </div>
                }
            </Centering>
        );
    }
}
